import { CapabilityDomain } from '../../../types/persona_engine';

// ── LOD zoom thresholds ───────────────────────────────────────────────────────
export const LOD_CLUSTER  = 0.35;  // below → cluster bubbles only
export const LOD_NODES    = 2.00;  // below → shapes + labels, no HTML overlay
// above LOD_NODES → full HTML overlay (requires deliberate zoom-in)

// ── Cluster layout ────────────────────────────────────────────────────────────
export interface ClusterPosition {
  x: number;
  y: number;
  color: string;
  label: string;
}

/**
 * Compute cluster positions relative to canvas dimensions.
 * ARTIFACT (system_prompt) sits at center; everything else orbits it.
 * Call this whenever the canvas resizes.
 */
export function computeClusterPositions(W: number, H: number): Record<string, ClusterPosition> {
  const cx = W / 2;
  const cy = H / 2;
  return {
    ARTIFACT:      { x: cx,           y: cy,           color: '#22c55e', label: 'Artifact'      },
    IDENTITY_CORE: { x: cx,           y: cy - 230,     color: '#3b82f6', label: 'Identity Core' },
    TUNING:        { x: cx + 290,     y: cy - 90,      color: '#a855f7', label: 'Tuning'        },
    PROBES:        { x: cx + 230,     y: cy + 190,     color: '#f59e0b', label: 'Probes'        },
    CAPABILITIES:  { x: cx - 230,     y: cy + 190,     color: '#06b6d4', label: 'Capabilities'  },
    LINEAGE:       { x: cx - 290,     y: cy - 90,      color: '#6b7280', label: 'Lineage'       },
  };
}

/**
 * Fallback static positions used before canvas dimensions are known.
 * These are replaced by computeClusterPositions() at runtime.
 */
export const CLUSTER_POSITIONS: Record<string, ClusterPosition> =
  computeClusterPositions(1200, 700);

// ── Node radius by type (visual hierarchy) ────────────────────────────────────
export const NODE_RADIUS: Record<string, number> = {
  system_prompt:      36,
  character_bible:    24,
  controller_prompt:  18,
  capability:         16,
  probe_cluster:      72,
  capability_cluster: 80,
  controls_cluster:   90,
  identity_cluster:   90,
  lineage_cluster:    70,  // dynamic — overridden at build time
  probe:              14,
  tethic:             13,
  pattern:            13,
  antipattern:        13,
  ui_control:         12,
  tag:                9,
  suggested_name:     8,
  origin_id:          8,
};

// ── Node type → cluster mapping ───────────────────────────────────────────────
export type NodeType =
  | 'character_bible'
  | 'tethic'
  | 'pattern'
  | 'antipattern'
  | 'ui_control'
  | 'system_prompt'
  | 'controller_prompt'
  | 'probe_cluster'
  | 'probe'
  | 'capability'
  | 'capability_cluster'
  | 'controls_cluster'
  | 'identity_cluster'
  | 'lineage_cluster'
  | 'tag'
  | 'suggested_name'
  | 'origin_id';

export const NODE_CLUSTER: Record<NodeType, string> = {
  character_bible:    'IDENTITY_CORE',
  tethic:             'IDENTITY_CORE',
  pattern:            'IDENTITY_CORE',
  antipattern:        'IDENTITY_CORE',
  identity_cluster:   'IDENTITY_CORE',
  ui_control:         'TUNING',
  controls_cluster:   'TUNING',
  system_prompt:      'ARTIFACT',
  controller_prompt:  'ARTIFACT',
  probe_cluster:      'PROBES',
  probe:              'PROBES',
  capability:         'CAPABILITIES',
  capability_cluster: 'CAPABILITIES',
  lineage_cluster:    'LINEAGE',
  tag:                'LINEAGE',
  suggested_name:     'LINEAGE',
  origin_id:          'LINEAGE',
};

// ── Node shape colors ─────────────────────────────────────────────────────────
export const NODE_COLORS: Record<NodeType, string> = {
  character_bible:    '#3b82f6',
  tethic:             '#ef4444',
  pattern:            '#22c55e',
  antipattern:        '#f97316',
  ui_control:         '#a855f7',
  controls_cluster:   '#a855f7',
  identity_cluster:   '#3b82f6',
  system_prompt:      '#22c55e',
  controller_prompt:  '#16a34a',
  probe_cluster:      '#f59e0b',
  probe:              '#f59e0b',
  capability:         '#06b6d4',
  capability_cluster: '#06b6d4',
  lineage_cluster:    '#6b7280',
  tag:                '#6b7280',
  suggested_name:     '#9ca3af',
  origin_id:          '#9ca3af',
};

// ── Tab jump map ──────────────────────────────────────────────────────────────
export const NODE_TAB_MAP: Record<NodeType, { tabIndex: number; scrollTarget?: string }> = {
  character_bible:    { tabIndex: 0 },
  tethic:             { tabIndex: 0, scrollTarget: 'tethics' },
  pattern:            { tabIndex: 0, scrollTarget: 'patterns' },
  antipattern:        { tabIndex: 0, scrollTarget: 'antipatterns' },
  identity_cluster:   { tabIndex: 0 },
  ui_control:         { tabIndex: 1 },
  controls_cluster:   { tabIndex: 1 },
  system_prompt:      { tabIndex: 2 },
  controller_prompt:  { tabIndex: 2 },
  probe_cluster:      { tabIndex: 3 },
  probe:              { tabIndex: 3 },
  capability:         { tabIndex: 4 },
  capability_cluster: { tabIndex: 4 },
  lineage_cluster:    { tabIndex: 0 },
  tag:                { tabIndex: 0 },
  suggested_name:     { tabIndex: 0 },
  origin_id:          { tabIndex: 0 },
};

// ── Tag accent colors (mirrors PersonaCard TAG_ACCENT_MAP) ────────────────────
export const TAG_ACCENT_RULES: Array<{ keywords: string[]; color: string }> = [
  { keywords: ['tech', 'code', 'engineer', 'dev', 'software'], color: '#3b82f6' },
  { keywords: ['creative', 'write', 'story', 'content', 'author'], color: '#a855f7' },
  { keywords: ['data', 'analytics', 'science', 'ml', 'ai'],       color: '#6366f1' },
  { keywords: ['design', 'ux', 'visual', 'ui', 'art'],            color: '#ec4899' },
  { keywords: ['finance', 'legal', 'compliance', 'law', 'audit'], color: '#f59e0b' },
  { keywords: ['health', 'medical', 'clinical', 'bio'],           color: '#10b981' },
];
export const TAG_DEFAULT_COLOR = '#6b7280';

export function getTagColor(tag: string): string {
  const lower = tag.toLowerCase();
  for (const { keywords, color } of TAG_ACCENT_RULES) {
    if (keywords.some(k => lower.includes(k))) return color;
  }
  return TAG_DEFAULT_COLOR;
}

// ── Identity cluster pillar colors ────────────────────────────────────────────
export const PILLAR_COLORS = {
  character_bible: '#3b82f6',
  tethic:          '#ef4444',
  pattern:         '#22c55e',
  antipattern:     '#f97316',
} as const;
